import { useState, useContext } from "react";
import { Grid } from "@mui/material";
import { convertToJalali } from "../../../../utils/date";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ItineraryContext } from "../../../../constants/layouts/itinerary-layout";
import { LocaleContext } from "../../../../routing/LangRouter";
import { toast } from "react-toastify";
import moment from "moment";
import { numberWithCommas } from "../../../../utils/utility";
import { useTranslation } from "react-i18next";
import { saveSelectedTicket, saveTicketSelectedFilters } from "../../../../store/actions";
import { connect } from "react-redux";

//import general components
import { BaseButton } from "../../../common/base-button/base-button";
import MobileHeader from "../../../common/mobile-header/mobile-header";
import BaseTabs from "../../../common/base-tabs/base-tabs";

//import view components
import BaggageRulesModal from "../international/int-flight-baggage";
import AvailabiltyModal from "../../availability/availabilty-modal";

//import styles
import "./flight-details.scss";

//import images
import flightLine from "../../../../assets/icons/flight-line.svg";
import refundRules from "../../../../assets/imgs/refund-rules2.png";

import { flightDataMapper } from "../../../../utils/flightDataMapper";

function FlightDetails({ dispatch }) {
    //general variables
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const flightDetails = JSON.parse(sessionStorage.getItem("flightDetails"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const generalInformation = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

    //general variables
    const adultCount = domesticFlightInformation?.personCounter.adult;
    const childCount = domesticFlightInformation?.personCounter.child;
    const infantCount = domesticFlightInformation?.personCounter.infant;
    const isTwoWay = domesticFlightInformation?.returnDate;
    const normalizedDepartureDetails = flightDataMapper(isTwoWay, flightDetails, flightDetails?.segments);
    const normalizedReturnDetails = flightDataMapper(isTwoWay, flightDetails, flightDetails?.return_segments);

    //handle tab change
    const [activeTab, setActiveTab] = useState({ departure: true, return: false, cancelRules: false, baggageRules: false });
    const handleTabChange = type => {
        setActiveTab({ [type]: true });
    };

    //handle opening modal
    const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
    const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);

    const normalizedDetails = activeTab?.departure ? normalizedDepartureDetails : normalizedReturnDetails;

    //list of tabs
    const tabList = [
        { type: "departure", title: t("departureFlight"), activeTab: activeTab?.departure },
        { type: "return", title: t("returnFlight"), activeTab: activeTab?.return, disable: !flightDetails?.return_segments?.length },
        { type: "cancelRules", title: t("refundRules"), activeTab: activeTab?.cancelRules },
        { type: "baggageRules", title: t("baggageRules"), activeTab: activeTab?.baggageRules },
    ];

    //check flight capacity
    const checkFlightCapacity = capacity => {
        const totalPersons = domesticFlightInformation.personCounter.adult + domesticFlightInformation.personCounter.child;
        if (capacity < totalPersons) {
            toast.error(t("flightMessage1"), { toastId: "custom-id-yes" });
            return false;
        } else return true;
    };

    const [baggageInfo, setBaggageInfo] = useState(null);

    //change the value to regenerate the itinerary
    const updateItineraryGenerate = () => {
        if (isItinerary) {
            itineraryInformation.itineraryGenerate = true;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        }
    };

    //update the itinerary date and time based on the selected flight
    const updateItineraryDateTime = (type, date, time, hours) => {
        const fullDate = moment(`${date} ${time.split(":", 1).join(":00")}`)
            .add(hours, "hours")
            .format()
            .split("+")[0];
        const [newDate, newTime] = fullDate.split("T");
        if (type === "departure") {
            itineraryInformation.departureDate = newDate;
            itineraryInformation.firstDayTime = newTime;
        } else if (type === "return") {
            itineraryInformation.returnDate = newDate;
            itineraryInformation.lastDayTime = newTime;
        }
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
    };

    //navigate to the next page
    const navigateNextPage = () => {
        const isAccommodation = generalInformation?.isAccommodation;
        setRoutes([...routes, `/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`]);
        sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, `/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`]));
        navigate(`/${locale}/itinerary/${isAccommodation ? "hotel" : "itinerary-page"}`, { replace: true });
    };

    //handle filter cancellation
    const resetFilters = () => {
        dispatch(saveTicketSelectedFilters(null));
    };

    //handle selected ticket
    const handleSelectedTicket = async ticket => {
        const normalizedDepartureTicket = flightDataMapper(isTwoWay, ticket, ticket?.segments);
        const normalizedReturnTicket = flightDataMapper(isTwoWay, ticket, ticket?.return_segments);

        if (!checkFlightCapacity(ticket.capacity)) return;
        updateItineraryGenerate();
        resetFilters();

        sessionStorage.setItem("selectedFlight", JSON.stringify(ticket));
        if (isItinerary) {
            updateItineraryDateTime("departure", normalizedDepartureTicket?.departureDate, normalizedDepartureTicket?.departureTime, 5);
            if (!!isTwoWay) {
                updateItineraryDateTime("return", normalizedReturnTicket?.departureDate, normalizedReturnTicket?.departureTime, -3);
            }
            navigateNextPage();
        } else navigate(`/${locale}/flight-passengers`);
    };

    const handleReserve = () => {
        dispatch(saveSelectedTicket(flightDetails));
        setIsReserveModalOpen(true);
    };

    const goToBack = () => {
        searchParams.delete("action");
        setSearchParams(searchParams, { replace: true });
        sessionStorage.removeItem("flightDetails");
    };

    return (
        <Grid className="flight-details" container item xs={12} rowGap={2}>
            <MobileHeader title={t("flightDetails")} justifyContent="center" handleClick={goToBack} />

            <BaseTabs handleTabChange={handleTabChange} list={tabList} style={{ top: isItinerary ? "6rem" : "3rem" }} />

            {activeTab.departure || activeTab.return ? (
                <Grid container item xs={12} rowGap={5} justifyContent="center">
                    <Grid container item xs={12}>
                        <Grid container item xs={1}>
                            <img src={flightLine} alt="flight" />
                        </Grid>

                        <Grid container item xs={3}>
                            <Grid container item xs={12} direction="column">
                                <span className="flight-details__time">{normalizedDetails?.departureTime}</span>
                                <span className="flight-details__date">
                                    {locale === "fa" ? convertToJalali(normalizedDetails?.departureDate) : normalizedDetails?.departureDate}
                                </span>
                            </Grid>
                            <Grid container item xs={12} direction="column" justifyContent="flex-end">
                                <span className="flight-details__time">{normalizedDetails?.destinationTime}</span>
                                <span className="flight-details__date">
                                    {locale === "fa" ? convertToJalali(normalizedDetails?.departureDate) : normalizedDetails?.departureDate}
                                </span>
                            </Grid>
                        </Grid>

                        <Grid container item xs={8}>
                            <Grid className="flight-details__location" container item xs={12}>
                                {locale === "en" ? normalizedDetails?.departureEnName : normalizedDetails?.departureFaName} - (
                                {normalizedDetails?.departureIata}) {normalizedDetails?.departureTerminal && "-"}{" "}
                                {normalizedDetails?.departureTerminal}
                            </Grid>
                            <Grid className="flight-details__location" container item xs={12} alignItems="flex-end">
                                {locale === "en" ? normalizedDetails?.destinationEnName : normalizedDetails?.destinationFaName} - (
                                {normalizedDetails?.destinationIata}) {normalizedDetails?.destinationTerminal && "-"}{" "}
                                {normalizedDetails?.destinationTerminal}
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid className="flight-details__details" container item xs={12}>
                        <Grid className="flight-details__header" container item xs={12} direction="column">
                            <span className="flight-details__title">{t("airline")}</span>
                            <span className="flight-details__airline">
                                <img src={normalizedDetails?.airlineLogo} alt="flight" />
                                {locale === "en" ? normalizedDetails?.airline : normalizedDetails?.airlinePersian}
                            </span>
                        </Grid>

                        <Grid className="flight-details__information" container item xs={12} rowGap={1}>
                            <Grid container item xs={4} direction="column">
                                <span className="flight-details__title">{t("flightType")}</span>
                                <span className="flight-details__name">
                                    {locale === "en" ? normalizedDetails?.type : normalizedDetails?.persianType}
                                </span>
                            </Grid>
                            <Grid container item xs={4} direction="column">
                                <span className="flight-details__title">{t("flightNumber")}</span>
                                <span className="flight-details__name">{normalizedDetails?.flightNumber}</span>
                            </Grid>
                            <Grid container item xs={4} direction="column">
                                <span className="flight-details__title">{t("aircraft")}</span>
                                <span className="flight-details__name">{normalizedDetails?.aircraft}</span>
                            </Grid>
                            <Grid container item xs={4} direction="column">
                                <span className="flight-details__title">{t("class")}</span>
                                <span className="flight-details__name">{normalizedDetails?.class}</span>
                            </Grid>
                            <Grid container item xs={4} direction="column">
                                <span className="flight-details__title">{t("baggage")}</span>
                                <span className="flight-details__name">{normalizedDetails?.baggage > 0 ? normalizedDetails?.baggage : "-"}</span>
                            </Grid>
                            <Grid container item xs={4} direction="column">
                                <span className="flight-details__title">{t("flightClass")}</span>
                                <span className="flight-details__name">
                                    {locale === "en" ? normalizedDetails?.cabin : normalizedDetails?.cabinPersian}
                                </span>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid className="flight-details__details" container item xs={12}>
                        <Grid className="flight-details__header" container item xs={12} direction="column">
                            <span className="flight-details__price-title">{t("price")}</span>
                        </Grid>

                        <Grid className="flight-details__price-details" container item xs={12} rowGap={1}>
                            {adultCount > 0 ? (
                                <Grid container item xs={12} justifyContent="space-between">
                                    <span>
                                        {adultCount} {t("adult")}:{" "}
                                    </span>
                                    <span>
                                        {numberWithCommas(normalizedDetails?.adultPrice * adultCount)} {t("currency")}
                                    </span>
                                </Grid>
                            ) : null}
                            {childCount > 0 ? (
                                <Grid container item xs={12} justifyContent="space-between">
                                    <span>
                                        {childCount} {t("child")}:{" "}
                                    </span>
                                    <span>
                                        {numberWithCommas(normalizedDetails?.childPrice * childCount)} {t("currency")}
                                    </span>
                                </Grid>
                            ) : null}
                            {infantCount > 0 ? (
                                <Grid container item xs={12} justifyContent="space-between">
                                    <span>
                                        {infantCount} {t("infant")}:{" "}
                                    </span>
                                    <span>
                                        {numberWithCommas(normalizedDetails?.infantPrice * infantCount)} {t("currency")}
                                    </span>
                                </Grid>
                            ) : null}
                        </Grid>

                        <Grid className="flight-details__total-price" container item xs={12} justifyContent="space-between">
                            <span>{t("total")}:</span>
                            <span>
                                {numberWithCommas(
                                    normalizedDetails?.adultPrice * adultCount +
                                        normalizedDetails?.childPrice * childCount +
                                        normalizedDetails?.infantPrice * infantCount,
                                )}{" "}
                                {t("currency")}
                            </span>
                        </Grid>
                    </Grid>
                </Grid>
            ) : activeTab.cancelRules ? (
                <Grid container item xs={12} rowGap={2}>
                    <Grid component="img" src={refundRules} container item xs={12} />
                </Grid>
            ) : activeTab?.baggageRules ? (
                <BaggageRulesModal
                    flightItem={flightDetails}
                    baggageInfo={baggageInfo}
                    setBaggageInfo={setBaggageInfo}
                    fareSourceCode={normalizedDetails?.fareSourceCode}
                    uniqueId=""
                />
            ) : null}

            <Grid className="flight-details__buttons" container item xs={12} justifyContent="flex-end">
                {flightDetails?.capacity === 0 ? (
                    <Grid container item justifyContent="space-between" columnGap={1}>
                        <Grid container item xs={5}>
                            <BaseButton
                                text={t("General.Notify When Available")}
                                color="primary"
                                size="large"
                                radius="radius-10"
                                handleClick={() => setIsNotificationModalOpen(true)}
                            />
                        </Grid>
                        <Grid container item xs={5}>
                            <BaseButton
                                text={t("General.Reserve When Available")}
                                color="primary"
                                size="large"
                                radius="radius-10"
                                handleClick={handleReserve}
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <BaseButton
                        text={t("Confirm and Continue")}
                        color="primary"
                        size="large"
                        radius="radius-10"
                        handleClick={() => handleSelectedTicket(flightDetails)}
                    />
                )}
            </Grid>
            <AvailabiltyModal
                flightItem={flightDetails}
                isModalOpen={isNotificationModalOpen}
                setIsModalOpen={setIsNotificationModalOpen}
                title={t("General.Notify When Available")}
                modalType="notification"
                pageUrl={`/${locale}/flight-availability`}
            />
            <AvailabiltyModal
                flightItem={flightDetails}
                isModalOpen={isReserveModalOpen}
                setIsModalOpen={setIsReserveModalOpen}
                title={t("General.Reserve When Available")}
                modalType="reserve"
                pageUrl={`/${locale}/flight-automatic-reserve`}
            />
        </Grid>
    );
}

export default connect()(FlightDetails);

// Revalidate flight

// const [revalidateLoading, setRevalidateLoading] = useState(false);
// const { revalidateBooking } = useRevalidate(generalInformation, setRevalidateLoading);

// const handleSelectedTicket = async ticket => {
//     let validationValue;
//     if (ticket.tag === "pr") {
//         await revalidateBooking(ticket?.fare_source_code, "domestic-flight").then(newPrice => {
//             validationValue = newPrice === ticket?.total_price ? true : false;
//         });
//     }

//Checks the zamzam flight selling type compatibility between departure and return flights.
// const checkSellingType = (departureSelling, returnSelling) => {
//     if (!returnSelling) return true;
//     if (departureSelling === "All" || returnSelling === "All") return true;
//     if (departureSelling === returnSelling) return true;
//     const incompatibleSellingTypes = {
//         "Only Iranian": {
//             "Non Iranian": {
//                 en: "Your selected flight is only for non-Iranian passengers.",
//                 fa: "پرواز انتخابی شما فقط برای مسافران غیرایرانی است.",
//             },
//         },
//         "Non Iranian": {
//             "Only Iranian": {
//                 en: "Your selected flight is only for Iranian passengers.",
//                 fa: "پرواز انتخابی شما فقط برای مسافران ایرانی است.",
//             },
//         },
//     };
//     if (incompatibleSellingTypes[departureSelling] && incompatibleSellingTypes[departureSelling][returnSelling]) {
//         const errorMessage =
//             locale === "en"
//                 ? incompatibleSellingTypes[departureSelling][returnSelling].en
//                 : incompatibleSellingTypes[departureSelling][returnSelling].fa;
//         toast.error(errorMessage, { toastId: "custom-id-yes" });
//         return false;
//     }
//     return true;
// };
